import React from 'react';
import { Modal, Icon, Input, Grid, Button, Radio } from 'semantic-ui-react';
import { connect } from 'react-redux';

import { FIND_MODES } from 'Pages/managemenu/helpers/Constants';
import { createToast, toastType } from 'Common/ToastHelper';
import { NOTIFICATION_DIALOG_MESSAGES } from 'Common/Constants';
import * as DialogActions from 'Components/dialogs/state/actions';
import { IngredientTableModal } from 'Pages/managerecipe/components/IngredientTableModal';
import { withIngredientApi } from 'Pages/managerecipe/components/IngredientTable';

import { UsedOnRecipeTable } from './UsedOnRecipeTable';
import { createErrorToast } from '../helpers/RemoveReplace';
import * as Actions from '../state/removereplaceingredient/actions';
import * as Selectors from '../state/removereplaceingredient/selectors';

import 'Styles/pages/managemenu/manageMenuModal.less';

const IngredientTableModalWithApi = withIngredientApi(IngredientTableModal, {
  searchOnMount: false,
  refreshTotalPages: true,
  useSecondaryPaging: true,
});

const ING_INITIAL_SEARCH_PARAMETERS = Object.freeze({
  Page: 1,
  PageSize: 10,
  Search: '',
  ShowInvLinked: false,
  ShowInactive: false,
});

export const RemoveReplaceModal = (props) => {
  const {
    getRecipesWhereIngredientIsUsed,
    clearIngredientRecipes,
    clearErrors,
    fetchRecipesError,
    removeIngredientError,
    setNotificationDialog,
    submitRemoveIngredient,
    submitReplaceIngredient,
    isSubmitSuccess,
    setSubmitSuccessful,
    replaceIngredientError,
    isRemoveReplaceModalOpen = () => {},
    onRemoveReplaceModalClose = () => {},
    recipes = [],
    fetchRecipesLoading = false,
    removeIngredientLoading = false,
    replaceIngredientLoading = false,
    ingredient = { Id: null, IngredientName: null },
  } = props;

  const [ingredientId] = React.useState(ingredient.Id);
  const [replaceIngredient, setReplaceIngredient] = React.useState(null);
  const [ingredientSearchParameters, setIngredientSearchParameters] =
    React.useState({ ...ING_INITIAL_SEARCH_PARAMETERS });
  const [showIngredientModal, setShowIngredientModal] = React.useState(false);
  const [selectedIds, setSelectedIds] = React.useState([]);
  const [mode, setMode] = React.useState(FIND_MODES.REMOVE);

  const loading =
    fetchRecipesLoading || removeIngredientLoading || replaceIngredientLoading;

  React.useEffect(() => {
    createErrorToast(
      fetchRecipesError,
      removeIngredientError,
      replaceIngredientError
    );
    clearErrors();
  }, [fetchRecipesError, removeIngredientError, replaceIngredientError]);

  React.useEffect(() => {
    if (ingredientId === null) {
      clearIngredientRecipes();
    }
    getRecipesWhereIngredientIsUsed(ingredientId);
  }, [ingredientId]);

  React.useEffect(() => {
    const successMessage = `${
      mode === FIND_MODES.REMOVE ? 'Removed' : 'Replaced'
    } Ingredient!`;
    if (isSubmitSuccess) {
      createToast(toastType.success, 'Success', successMessage);
      setSubmitSuccessful(false);
      handleClose();
    }
  }, [isSubmitSuccess]);

  const onRecipeTableChange = (selectedRecipeIds) => {
    setSelectedIds(selectedRecipeIds);
  };

  const handleClose = () => {
    clearIngredientRecipes();
    onRemoveReplaceModalClose();
  };

  const handleApplyChanges = () => {
    if (selectedIds.length === 0) {
      createToast(
        toastType.error,
        'Error',
        'At Least One Recipe Must be Selected'
      );
      return;
    }

    displayNotification();
  };

  const displayNotification = () => {
    if (mode === FIND_MODES.REMOVE) {
      setNotificationDialog({
        ...NOTIFICATION_DIALOG_MESSAGES.REMOVE_INGREDIENTS_FROM_RECIPES,
        actionsToDispatchOnOk: [],
        functionsToRunOnOk: [
          () => {
            submitRemoveIngredient(ingredientId, selectedIds);
          },
        ],
      });
      return;
    }
    setNotificationDialog({
      ...NOTIFICATION_DIALOG_MESSAGES.REPLACE_INGREDIENTS_ON_RECIPES,
      actionsToDispatchOnOk: [],
      functionsToRunOnOk: [
        () => {
          submitReplaceIngredient(
            ingredientId,
            replaceIngredient.Id,
            selectedIds
          );
        },
      ],
    });
  };
  const onSearchParametersChange = (searchParameters) =>
    setIngredientSearchParameters(searchParameters);

  const onAddIngredient = (selectedIngredient) => {
    setReplaceIngredient(selectedIngredient);
    setShowIngredientModal(false);
  };

  const onIngredientModalClose = () => {
    setShowIngredientModal(false);
  };

  return (
    <React.Fragment>
      <Modal open={isRemoveReplaceModalOpen}>
        <Modal.Header className="manage-menu-modal-header">
          Remove / Replace Ingredients
        </Modal.Header>
        <Icon
          name="close"
          onClick={handleClose}
          className="manage-menu-close"
        />
        <Modal.Content>
          <Grid>
            <Grid.Row>
              <Grid.Column width={8}>
                <label>Ingredient</label>
                <Input value={ingredient.IngredientName} fluid disabled />
              </Grid.Column>
            </Grid.Row>
            <Grid.Row>
              <Grid.Column>
                <UsedOnRecipeTable
                  recipeData={ingredientId ? recipes : []}
                  onChange={onRecipeTableChange}
                  loading={loading}
                />
              </Grid.Column>
            </Grid.Row>
            <Grid.Row>
              <Grid.Column>
                <label>With Selected Ingredient:</label>
                <Radio
                  label="Remove"
                  value={FIND_MODES.REMOVE}
                  checked={mode === FIND_MODES.REMOVE}
                  onChange={() => setMode(FIND_MODES.REMOVE)}
                />
                <Radio
                  label="Replace"
                  value={FIND_MODES.REPLACE}
                  checked={mode === FIND_MODES.REPLACE}
                  onChange={() => setMode(FIND_MODES.REPLACE)}
                  style={{ marginLeft: 16 }}
                />
              </Grid.Column>
            </Grid.Row>
            <Grid.Row>
              <Grid.Column width={8}>
                <label>Replace With:</label>
                <Input
                  disabled={mode !== FIND_MODES.REPLACE}
                  placeholder={'Search for ingredient to replace with'}
                  value={
                    replaceIngredient ? replaceIngredient.IngredientName : ''
                  }
                  onClick={() => {
                    setShowIngredientModal(true);
                  }}
                  fluid
                />
              </Grid.Column>
            </Grid.Row>
          </Grid>
        </Modal.Content>
        <Modal.Actions>
          <div className="manage-menu-header-buttons">
            <Button
              className="mnm-button-blue"
              onClick={onRemoveReplaceModalClose}
            >
              Close
            </Button>
            <Button
              className="mnm-button-blue"
              onClick={handleApplyChanges}
              loading={loading}
              disabled={loading || selectedIds.length === 0}
            >
              Apply Changes
            </Button>
          </div>
        </Modal.Actions>
      </Modal>
      <IngredientTableModalWithApi
        modalProps={{
          size: 'large',
          open: showIngredientModal,
          onClose: onIngredientModalClose,
        }}
        headerTitle={'Add Ingredient'}
        onAddIngredient={onAddIngredient}
        searchParameters={ingredientSearchParameters}
        onSearchParametersChange={onSearchParametersChange}
        manual={true}
        resetOnMount={true}
        doNotResetOnClose={true}
        hideOptionsMenu={true}
      />
    </React.Fragment>
  );
};

const mapStateToProps = (state) => {
  return {
    recipes: Selectors.getRecipes(state),
    fetchRecipesLoading: Selectors.getFetchRecipesLoading(state),
    removeIngredientLoading: Selectors.getRemoveIngredientLoading(state),
    replaceIngredientLoading: Selectors.getReplaceIngredientLoading(state),
    fetchRecipesError: Selectors.getFetchRecipesError(state),
    removeIngredientError: Selectors.getRemoveIngredientError(state),
    replaceIngredientError: Selectors.getReplaceIngredientError(state),
    isSubmitSuccess: Selectors.getIsSubmitSuccess(state),
  };
};

const mapDispatchToProps = { ...Actions, ...DialogActions };

const RemoveReplaceIngredientModal = connect(
  mapStateToProps,
  mapDispatchToProps
)(RemoveReplaceModal);

export { RemoveReplaceIngredientModal };



// WEBPACK FOOTER //
// ./src/pages/manageingredient/components/RemoveReplaceIngredientModal.js